import { Menu, Sun, Moon, Palette, PanelLeftClose, PanelLeft, Maximize2, Minimize2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useThemeStore } from '../../stores/themeStore';
import { useUiStore } from '../../stores/uiStore';
import UserMenu from './UserMenu';
import TeachButton from './TeachButton';

interface TopBarProps {
  // Title shown center — current course / lesson title resolved by the page.
  title?: string;
}

const THEME_ICON = { paper: Palette, light: Sun, dark: Moon } as const;
const THEME_NEXT = { paper: 'light', light: 'dark', dark: 'paper' } as const;

export default function TopBar({ title }: TopBarProps) {
  const theme = useThemeStore((s) => s.theme);
  const cycleTheme = useThemeStore((s) => s.cycleTheme);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const focusMode = useUiStore((s) => s.focusMode);
  const toggleFocusMode = useUiStore((s) => s.toggleFocusMode);

  const ThemeIcon = THEME_ICON[theme];

  return (
    <header className="flex h-11 items-center gap-1 sm:gap-2 border-b border-ws-nav-border bg-ws-nav px-3 sm:px-4 shrink-0">
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileNavOpen(true)}
        className="lg:hidden shrink-0 grid size-8 place-items-center rounded-lg text-ws-ink-2 hover:bg-ws-surface-2 transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Desktop sidebar toggle — removes the whole LeftNav panel (course
          picker + week tree), not just the week list inside it. Named "Hide
          sidebar" / "Show sidebar" rather than the old "Collapse navigation":
          that wording was easy to confuse with LeftNav's own "Collapse all
          weeks" button, which only folds the week groups and leaves the
          panel itself in place — three overlapping "collapse" affordances
          with near-identical names is exactly what made this hard to find. */}
      <button
        onClick={toggleNav}
        className="hidden lg:inline-flex shrink-0 p-2 rounded-lg text-ws-ink-3 hover:bg-ws-surface-2 hover:text-ws-ink-2 transition-colors"
        aria-label={navCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        title={navCollapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        {navCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
      </button>

      {/* Brand */}
      <Link
        to="/"
        className="flex items-center gap-2.5 shrink-0 group rounded-lg -mx-1 px-1 py-1"
      >
        <span
          className="relative size-7 rounded-lg text-white grid place-items-center text-[13px] font-extrabold tracking-tight shadow-sm transition-transform duration-200 group-hover:scale-105"
          style={{
            backgroundImage:
              'linear-gradient(135deg, var(--ws-accent) 0%, var(--ws-dark-teal) 100%)',
          }}
        >
          A
          {/* Gold accent dot — the brand's secondary color */}
          <span
            className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-ws-nav"
            style={{ background: 'var(--ws-gold)' }}
          />
        </span>
        <span className="hidden sm:flex flex-col leading-none">
          <span className="text-[15px] font-bold tracking-tight text-ws-ink">
            ACE <span className="text-ws-accent">Core</span>
          </span>
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-ws-ink-3">
            Chemistry
          </span>
        </span>
      </Link>

      {/* The centre title pill that used to sit here is gone. TopNav names the
          current destination and marks it aria-current, so repeating it here
          said the same thing twice and cost a row of height in the chrome the
          instructor looks past all class. This spacer keeps the brand left and
          the controls right. */}
      <div className="flex-1 min-w-0" />

      {/* Right — Teach + focus mode + theme toggle + user */}
      <div className="ml-auto flex items-center gap-1 shrink-0">
        {/* Instructor-only; renders nothing for a student. Moved here from the
            destination row at the instructor's request — it is a MODE switch,
            and this is where the other mode controls live. */}
        <TeachButton />
        {/* One control for what used to be three: the destination row, the
            live-class row, the sidebar and the lecture's slide rail all go at
            once. This bar deliberately STAYS, so the way back is always in the
            same place — no floating button over the content. Escape also
            leaves. */}
        <button
          onClick={toggleFocusMode}
          className={`grid size-8 place-items-center rounded-lg transition-colors hover:bg-ws-surface-2 hover:text-ws-ink-2 ${
            focusMode ? 'bg-ws-surface-2 text-ws-ink' : 'text-ws-ink-3'
          }`}
          title={focusMode ? 'Leave focus mode (Esc)' : 'Focus mode — hide the navigation'}
          aria-label={focusMode ? 'Leave focus mode' : 'Enter focus mode'}
          aria-pressed={focusMode}
        >
          {focusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        <button
          onClick={cycleTheme}
          className="grid size-8 place-items-center rounded-lg text-ws-ink-3 hover:bg-ws-surface-2 hover:text-ws-ink-2 transition-colors"
          title={`Theme: ${theme} — switch to ${THEME_NEXT[theme]}`}
          aria-label={`Switch theme (current: ${theme})`}
        >
          <ThemeIcon size={17} />
        </button>
        <div className="pl-1 ml-1 border-l border-ws-border">
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
